import {
  ContentStatus,
  ExamElementFragment,
  ExamElementFragment_progression,
  ExamElementListFragment,
  FolderElementFragment,
  FolderElementFragment_progression,
  FolderElementListFragment,
  FolderType,
  StudyProgrammeElementFragment,
  StudyProgrammeElementListFragment,
} from "__generated__/types";
import { Tag } from "common/components/Tag";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { ProgressBar } from "common/components/progression/ProgressBar";
import { ProgressionCheckmark } from "common/components/progression/ProgressionCheckmark";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { urls } from "common/urls";
import {
  getElementIcon,
  getStudyProgrammeElementIcon,
  isPublished,
} from "domain/common/utils";
import { SelfAssessmentStarter } from "domain/self-assessment/SelfAssessmentStarter";
import { getStudyProgrammeElementUrl } from "domain/study-programme/utils";
import React, { Fragment, ReactElement } from "react";
import { Link } from "react-router-dom";
import styles from "./ContentElementList.module.css";

type Parent =
  | FolderElementListFragment
  | StudyProgrammeElementListFragment
  | ExamElementListFragment;

interface Props {
  parent: Parent;
  withGrid?: boolean;
}
export const ContentElementList: React.FC<React.PropsWithChildren<Props>> = ({
  parent,
  withGrid,
}) => {
  return (
    <ListContainer className={styles.contentElementList} withGrid={withGrid}>
      {GetParentElementList(parent).map((s) => (
        <Fragment key={s.key}>{s}</Fragment>
      ))}
    </ListContainer>
  );
};

const GetParentElementList = (parent: Parent): ReactElement[] => {
  const { myExamSyllabi } = useMyExamSyllabus();
  switch (parent.__typename) {
    case "Folder":
      const containsSelfAssessments = parent.elements.some(
        (e) =>
          e.content.__typename === "Folder" &&
          e.content.type === FolderType.SELF_ASSESSMENT_EXERCISES,
      );
      if (containsSelfAssessments) {
        return parent.elements
          .filter(
            (e) =>
              e.content.__typename === "Folder" &&
              e.content.type === FolderType.SELF_ASSESSMENT_EXERCISES,
          )
          .filter((e) => e.content.status === ContentStatus.PUBLISHED)
          .map((e) => mapFolderElement(e));
      } else {
        return parent.elements
          .filter((e) => isPublished(e.content))
          .map((e) => mapFolderElement(e));
      }
    case "StudyProgramme":
      const examSyllabusElements = parent.examSyllabusElements
        .filter((e) => isPublished(e.content))
        .filter((e) => e.content.status === ContentStatus.PUBLISHED)
        .map((syllabus) => ({
          ...syllabus,
          isStudentsSyllabus: myExamSyllabi
            ?.map((m) => m.id)
            .includes(syllabus.content.id),
        }));

      if (examSyllabusElements.length === 0) {
        return [
          <div key="none-available">
            Det er ikke lagt ut noe pensum i dette studieprogrammet enda.
          </div>,
        ];
      }

      return examSyllabusElements.map((e) =>
        mapStudyProgrammeElement(e, e.isStudentsSyllabus),
      );
    case "ExamSyllabus":
      const isSyllabus = myExamSyllabi?.map((e) => e.id).includes(parent.id);

      return parent.elements
        .filter((e) => isPublished(e.content))
        .filter((e) => e.content.status === ContentStatus.PUBLISHED)
        .map((e) => {
          return mapExamElement(e, isSyllabus);
        });
  }
};

// TODO: Rewrite all urls methods in this file to use utils from "url-utils.ts"

const getFolderElementUrl = (element: FolderElementFragment): string => {
  switch (element.content.__typename) {
    case "Kurssloeyfe":
      return urls.kurssloeyfe(element.content.id);
    case "Folder":
      if (element.content.type === FolderType.SELF_ASSESSMENT_EXERCISES) {
        return urls.selfAssessment(element.content.id); // TODO: This url is not used
      } else if (element.content.type === FolderType.GENERIC) {
        return urls.folder(element.content.id);
      }
    case "Article":
      return urls.article(element.content.id);
    case "MultipleChoice":
      return urls.question(element.content.id);
    default:
      throw Error(
        "Found an illegal element type inside folder, not able to generate url",
      );
  }
};

const getExamElementUrl = (element: ExamElementFragment): string => {
  switch (element.content.__typename) {
    case "Folder":
      return urls.folder(element.content.id);
    default:
      throw Error(
        "Found an illegal element type inside exam syllabus, not able to generate url",
      );
  }
};

// TODO: Could all mapping functions below use the same method to get icons?

const mapFolderElement = (element: FolderElementFragment): ReactElement => {
  const isSelfAssessment =
    element.content.__typename === "Folder" &&
    element.content.type === FolderType.SELF_ASSESSMENT_EXERCISES;

  return (
    <ContainerElementItem
      key={element.id}
      id={element.id}
      title={element.content.title}
      url={getFolderElementUrl(element)}
      icon={getElementIcon(element)}
      isLink={!isSelfAssessment}
      showFinished={element.finished}
      progression={element.progression}
    >
      {isSelfAssessment && (
        <SelfAssessmentStarter id={element.content.id} canStart={true} />
      )}
    </ContainerElementItem>
  );
};

const mapExamElement = (
  element: ExamElementFragment,
  isSyllabus?: boolean,
): ReactElement => {
  return (
    <ContainerElementItem
      id={element.id}
      title={element.content.title}
      url={getExamElementUrl(element)}
      icon={getElementIcon(element)}
      isLink
      progression={isSyllabus ? element.progression : undefined}
    />
  );
};

const mapStudyProgrammeElement = (
  element: StudyProgrammeElementFragment,
  isSyllabus?: boolean,
): ReactElement => {
  return (
    <ContainerElementItem
      id={element.id}
      title={element.content.title}
      url={getStudyProgrammeElementUrl(element)}
      icon={getStudyProgrammeElementIcon(element)}
      isLink
    >
      {isSyllabus && <Tag name="Mitt pensum" />}
    </ContainerElementItem>
  );
};

interface ContainerElementItemProps {
  id: string;
  title: string;
  url: string;
  icon: React.ReactNode;
  isLink: boolean;
  showFinished?: boolean | null;
  progression?:
    | FolderElementFragment_progression
    | ExamElementFragment_progression
    | null;
}

const ContainerElementItem: React.FC<
  React.PropsWithChildren<ContainerElementItemProps>
> = ({ id, url, title, icon, isLink, showFinished, progression, children }) => {
  const content = (
    <section className={styles.listElement}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        {icon}
        <span className={styles.text}>
          <h3 className={styles.title}>{title}</h3>
        </span>
        {children}
        {typeof showFinished === "boolean" && (
          <ProgressionCheckmark finished={showFinished} title={title} />
        )}
      </div>
      {typeof showFinished !== "boolean" && progression ? (
        <ProgressBar progression={progression} hideBarWhenCompleted />
      ) : null}
    </section>
  );

  return (
    <ListItem key={id} className={styles.listItem}>
      {isLink ? <Link to={url}>{content}</Link> : content}
    </ListItem>
  );
};
